<template>
  <div class="field-portal">
    <!-- Ce qui vient de se passer sur une fiche (pose rendue, problème signalé) : dit une fois, en haut. -->
    <v-alert v-if="notice" type="success" text dense dismissible class="mb-4" @input="dismissNotice">{{ $t(`onboarding.field.notice.${notice}`) }}</v-alert>

    <!-- Les deux métiers du technicien : poser, ou intervenir sur une borne. -->
    <v-tabs :value="mode === 'maintenance' ? 1 : 0" grow height="40" background-color="transparent" class="field-portal__modes mb-4" @change="switchMode($event === 1 ? 'maintenance' : 'commissioning')">
      <v-tab class="text-none"><v-icon small class="mr-1">mdi-flash</v-icon>{{ $t('onboarding.field.mode.commissioning') }}</v-tab>
      <v-tab class="text-none"><v-icon small class="mr-1">mdi-wrench-outline</v-icon>{{ $t('onboarding.field.mode.maintenance') }}</v-tab>
    </v-tabs>

    <field-maintenance-search v-if="mode === 'maintenance'" />

    <template v-else>
      <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>

      <!-- Chercher n'importe quelle pose ouverte de son périmètre, sans qu'on la lui ait confiée. -->
      <div class="d-flex align-center mb-5">
        <v-text-field
            v-model="search"
            :placeholder="$t('onboarding.field.search')"
            prepend-inner-icon="mdi-magnify"
            outlined dense clearable hide-details
            background-color="white"
        />
        <v-btn icon class="ml-1" :loading="loading" :title="$t('refresh')" @click="load"><v-icon>mdi-refresh</v-icon></v-btn>
      </div>

      <template v-if="searching">
        <h2 class="text-subtitle-1 font-weight-bold mb-3">{{ $t('onboarding.field.results') }}</h2>
        <p v-if="!loading && !found.length" class="text-body-2 text--secondary">{{ $t('onboarding.field.none-found') }}</p>
        <field-order-card v-for="order in orders.mine" :key="order.id" :order="order" :waiting="!order.readyForInstallation" />
        <field-order-card v-for="order in orders.open" :key="order.id" :order="order" />
        <field-order-card v-for="order in orders.notReady" :key="order.id" :order="order" waiting />
      </template>

      <!-- Sans recherche : seulement les siennes. Les poses libres se trouvent en cherchant, pas en liste. -->
      <template v-else>
        <h2 class="text-subtitle-1 font-weight-bold mb-3">{{ $t('onboarding.field.mine') }}</h2>
        <p v-if="!loading && !orders.mine.length" class="text-body-2 text--secondary mb-6">{{ $t('onboarding.field.none-mine') }}</p>
        <section v-for="group in mineByDay" :key="group.day" class="mb-5">
          <div class="field-portal__group">{{ $t(`onboarding.field.day.${group.day}`) }} · {{ group.orders.length }}</div>
          <field-order-card v-for="order in group.orders" :key="order.id" :order="order" :waiting="!order.readyForInstallation" />
        </section>
      </template>

      <!-- Pas de commande, pas de pose : c'est la supervision qui la crée. -->
      <p class="text-caption text--secondary mt-6 mb-1">{{ $t('onboarding.field.missing-text') }}</p>
      <div v-for="contact in supervision" :key="contact.region" class="text-caption">
        {{ $t(`onboarding.field.supervision-region.${contact.region}`) }} :
        <a v-if="contact.phone" :href="`tel:${contact.phone.replace(/\s/g, '')}`">{{ contact.phone }}</a>
        <a v-else :href="`mailto:${contact.email}`">{{ contact.email }}</a>
      </div>
    </template>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'
import FieldMixin from './FieldMixin'
import FieldOrderCard from './FieldOrderCard.vue'
import FieldMaintenanceSearch from './FieldMaintenanceSearch.vue'

/** Les jours de « Mes poses », dans l'ordre où le technicien les vit. Un retard se traite aujourd'hui. */
const DAYS = ['today', 'upcoming', 'undated']

/**
 * L'accueil du technicien : mise en service ou maintenance. En mise en service, ses poses (aujourd'hui, à venir, sans
 * date) ; ou, s'il cherche (adresse, nom, n° de commande), toutes les poses ouvertes de son périmètre qui y répondent :
 * on ne lui confie rien d'avance. Pas de commande, pas de pose : il appelle la supervision, qui la crée.
 */
export default {
  name: 'FieldPortal',

  components: { FieldOrderCard, FieldMaintenanceSearch },

  mixins: [CommissioningMixin, FieldMixin],

  data() {
    return { orders: { mine: [], open: [], notReady: [] }, search: '', loading: false, error: null, ticket: 0, supervision: [] }
  },

  computed: {
    mode() {
      return this.$route.query.mode === 'maintenance' ? 'maintenance' : 'commissioning'
    },

    /** Deux caractères au moins : une lettre seule ramène tout le pays. */
    searching() {
      return (this.search || '').trim().length >= 2
    },

    found() {
      return [...this.orders.mine, ...this.orders.open, ...this.orders.notReady]
    },

    /** Le serveur trie par rendez-vous : on garde son ordre dans chaque jour. */
    mineByDay() {
      const groups = DAYS.map(day => ({ day, orders: [] }))
      this.orders.mine.forEach(order => {
        const day = this.dayOf(order) === 'late' ? 'today' : this.dayOf(order)
        groups.find(g => g.day === day).orders.push(order)
      })
      return groups.filter(g => g.orders.length)
    },

    notice() {
      return this.$route.query.notice || null
    },

  },

  watch: {
    search() {
      this.debouncedLoad()
    },
  },

  created() {
    this.debouncedLoad = debounce(this.load, 400)
    // Qui appeler quand la pose n'existe pas : les supervisions ZEborne (France, autres pays).
    FieldRepository.supervision().then(contacts => { this.supervision = contacts || [] }).catch(() => {})
    if (this.mode === 'commissioning') {
      this.load()
    }
  },

  methods: {
    async load() {
      // Une recherche partie avant la dernière frappe ne remplace pas la plus récente.
      const ticket = ++this.ticket
      this.loading = true
      this.error = null
      try {
        const orders = await FieldRepository.orders(this.searching ? this.search.trim() : null)
        if (ticket !== this.ticket) return
        this.orders = { mine: orders.mine || [], open: orders.open || [], notReady: orders.notReady || [] }
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-list')
      } finally {
        this.loading = false
      }
    },

    switchMode(mode) {
      this.$router.replace({ name: 'field', query: mode === 'maintenance' ? { mode } : {} })
      if (mode === 'commissioning') {
        this.load()
      }
    },

    dismissNotice() {
      this.$router.replace({ name: 'field', query: this.mode === 'maintenance' ? { mode: 'maintenance' } : {} })
    },
  },
}
</script>

<style scoped>
.field-portal__group {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}
</style>
